import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Calendar,
  Clock,
  Award,
  CheckCircle,
  Users,
  Play,
  ArrowRight,
  Loader2,
  User,
  Star,
} from 'lucide-react';
import { format } from 'date-fns';
import { formatET } from '@/utils/dateUtils';
import api from '@/services/api';

export default function LandingPageView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPage();
  }, [slug]);

  const fetchPage = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/landing-pages/public/${slug}`);
      setPage(response.data);
    } catch (error) {
      console.error('Error fetching landing page:', error);
      setError('Landing page not found');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'TBD';
    return format(new Date(dateStr), 'EEEE, MMMM d, yyyy \'at\' h:mm a');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-navy-600" />
      </div>
    );
  }

  if (error || !page) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <h2 className="font-heading text-xl font-semibold text-navy-700 mb-2">
              Page Not Found
            </h2>
            <p className="text-slate-600 mb-4">
              This landing page doesn't exist or has been disabled.
            </p>
            <Link to="/">
              <Button className="bg-navy-600 hover:bg-navy-700">
                Go to Homepage
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const colors = page.color_scheme || {
    primary: '#1B4F72',
    secondary: '#D4A03A',
    accent: '#1B4F72',
  };

  return (
    <div className="min-h-screen bg-white" data-testid="landing-page-view">
      {/* Custom CSS */}
      {page.custom_css && <style>{page.custom_css}</style>}

      {/* Hero Section */}
      <section
        className="hero relative py-20 lg:py-32"
        style={{
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <Badge className="bg-white/20 text-white mb-4">
                Live Webinar
              </Badge>
              <h1 className="font-heading text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                {page.hero_headline || page.title}
              </h1>
              <p className="text-xl text-white/90 mb-8 leading-relaxed">
                {page.hero_subheadline}
              </p>
              
              {/* Event Details */}
              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                  <Calendar className="w-5 h-5" />
                  <span>{page.scheduled_date ? formatET(page.scheduled_date, 'MMM d, yyyy') : 'Date TBD'}</span>
                </div>
                <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                  <Clock className="w-5 h-5" />
                  <span>{page.duration_minutes || 60} Minutes</span>
                </div>
                {page.cpe_credits > 0 && (
                  <div className="flex items-center gap-2 bg-white/10 rounded-lg px-4 py-2">
                    <Award className="w-5 h-5" />
                    <span>{page.cpe_credits} CPE Credits</span>
                  </div>
                )}
              </div>
              
              <Link to={`/webinars/${page.webinar_id}`}>
                <Button
                  size="lg"
                  className="text-lg px-8 py-6 rounded-xl font-semibold"
                  style={{ backgroundColor: colors.secondary, color: colors.primary }}
                >
                  {page.hero_cta_text || 'Register Now'}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
            
            {/* Hero Image */}
            {page.hero_image_url && (
              <div className="hidden lg:block">
                <img
                  src={page.hero_image_url}
                  alt={page.title}
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            )}
          </div>
        </div>
      </section>

      {/* About Section */}
      {page.about_content && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <h2
                className="font-heading text-3xl font-bold mb-6 text-center"
                style={{ color: colors.primary }}
              >
                About This Webinar
              </h2>
              <div
                className="prose prose-lg max-w-none text-slate-600"
                dangerouslySetInnerHTML={{ __html: page.about_content }}
              />
            </div>
          </div>
        </section>
      )}

      {/* Speaker Section */}
      {page.speakers && page.speakers.length > 0 && (
        <section className="py-16 lg:py-24 bg-slate-50">
          <div className="container mx-auto px-4">
            <h2
              className="font-heading text-3xl font-bold mb-12 text-center"
              style={{ color: colors.primary }}
            >
              Meet Your Instructor
            </h2>
            <div className="max-w-4xl mx-auto">
              {page.speakers.map((speaker, index) => (
                <div key={index} className="flex flex-col md:flex-row items-center gap-8">
                  <div className="w-48 h-48 rounded-full overflow-hidden flex-shrink-0 border-4 border-white shadow-xl">
                    {speaker.image_url ? (
                      <img
                        src={speaker.image_url}
                        alt={speaker.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center text-4xl font-bold text-white"
                        style={{ backgroundColor: colors.primary }}
                      >
                        {speaker.name?.split(' ').map(n => n[0]).join('')}
                      </div>
                    )}
                  </div>
                  <div className="text-center md:text-left">
                    <h3
                      className="font-heading text-2xl font-bold mb-2"
                      style={{ color: colors.primary }}
                    >
                      {speaker.name}
                    </h3>
                    <p className="text-lg text-slate-600 mb-4">{speaker.headline}</p>
                    <p className="text-slate-500">{speaker.bio}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Learning Outcomes */}
      {page.learning_outcomes && page.learning_outcomes.length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <h2
              className="font-heading text-3xl font-bold mb-12 text-center"
              style={{ color: colors.primary }}
            >
              What You'll Learn
            </h2>
            <div className="max-w-3xl mx-auto grid md:grid-cols-2 gap-6">
              {page.learning_outcomes.map((outcome, index) => (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 rounded-xl bg-slate-50"
                >
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: colors.secondary }}
                  >
                    <CheckCircle className="w-5 h-5" style={{ color: colors.primary }} />
                  </div>
                  <p className="text-slate-700">{outcome}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Pricing / Variants */}
      {page.variants && page.variants.length > 0 && (
        <section
          className="py-16 lg:py-24"
          style={{ backgroundColor: colors.primary }}
        >
          <div className="container mx-auto px-4">
            <h2 className="font-heading text-3xl font-bold mb-12 text-center text-white">
              Choose Your Access
            </h2>
            <div className="max-w-5xl mx-auto grid md:grid-cols-3 gap-6">
              {page.variants.slice(0, 3).map((variant, index) => (
                <Card
                  key={index}
                  className={`border-0 ${index === 1 ? 'ring-4 ring-gold-400 scale-105' : ''}`}
                >
                  <CardContent className="p-6 text-center">
                    {index === 1 && (
                      <Badge className="bg-gold-100 text-gold-700 mb-4">
                        Most Popular
                      </Badge>
                    )}
                    <h3 className="font-heading text-xl font-bold text-navy-700 mb-2">
                      {variant.name}
                    </h3>
                    <p className="text-3xl font-bold text-navy-700 mb-4">
                      ${variant.price}
                    </p>
                    <p className="text-sm text-slate-600 mb-6">
                      {variant.description || 'Full access to the webinar'}
                    </p>
                    <Link to={`/webinars/${page.webinar_id}`}>
                      <Button
                        className="w-full"
                        style={{
                          backgroundColor: index === 1 ? colors.secondary : colors.primary,
                          color: index === 1 ? colors.primary : 'white',
                        }}
                      >
                        Select
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* FAQ Section */}
      {page.faq_items && page.faq_items.length > 0 && (
        <section className="py-16 lg:py-24 bg-white">
          <div className="container mx-auto px-4">
            <h2
              className="font-heading text-3xl font-bold mb-12 text-center"
              style={{ color: colors.primary }}
            >
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto">
              <Accordion type="single" collapsible>
                {page.faq_items.map((faq, index) => (
                  <AccordionItem key={index} value={`faq-${index}`}>
                    <AccordionTrigger className="text-left font-medium text-navy-700">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-600">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      {/* Final CTA */}
      <section
        className="py-16 lg:py-24"
        style={{
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`,
        }}
      >
        <div className="container mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl lg:text-4xl font-bold text-white mb-6">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto">
            Join thousands of professionals who have enhanced their skills with our webinars.
          </p>
          <Link to={`/webinars/${page.webinar_id}`}>
            <Button
              size="lg"
              className="text-lg px-8 py-6 rounded-xl font-semibold"
              style={{ backgroundColor: colors.secondary, color: colors.primary }}
            >
              {page.hero_cta_text || 'Register Now'}
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 text-center">
        <p className="text-slate-400 text-sm">
          © {new Date().getFullYear()} GRC Educators. All rights reserved.
        </p>
      </footer>
    </div>
  );
}
